import { Badge, Box, Flex } from "@chakra-ui/react";
import { PROOF } from "./homeContent";
import { Body, CellGrid, H3, Inner, Intro, Mono, Section, alpha, useHomeTokens } from "./primitives";

/**
 * #proof — "Where this is going". Steps 01 and 02 are in production. Steps 03
 * and 04 are the research direction that ordinary engagements fund — they are
 * NOT shipped, and this section must never read as if they were (hence the
 * per-step badge on top of the design's footnote).
 */
export const Proof = () => {
  const t = useHomeTokens();
  return (
    <Section id="proof" alt>
      {/* Alias for links that predate the redesign. */}
      <Box id="where-this-is-going" position="absolute" top={0} aria-hidden="true" />
      <Box
        position="absolute"
        left="-15%"
        right="-15%"
        bottom="-50%"
        h="700px"
        pointerEvents="none"
        bg={`radial-gradient(45% 50% at 50% 100%, ${alpha(t.primary, 0.12)} 0%, transparent 70%)`}
      />
      <Inner>
        <Intro
          kicker="Where this is going"
          title="Eventually we stop writing terms and start sharing schemas"
          maxW="760px"
          mb="18px"
          lead={
            <>
              Almost every dispute in business is two parties holding different versions of the same agreement. The
              long-run goal of this platform is to remove that failure mode: instead of exchanging documents and trusting
              the interpretation, both sides hold the same schema and use blockchain attestation to verify, formally,
              that they are on the same page.
            </>
          }
        />
        <Body fontSize="1.05rem" lineHeight="1.65" maxW="760px" mb="56px">
          That work is funded by ordinary engagements. Every client on a managed plan, every module built, every hour of
          consulting pays for the research underneath it — which is why the terms are published, the price list is
          single, and the exits are named. You cannot build verifiable agreements on a business that hides its own.
        </Body>

        <CellGrid columns={{ base: 1, md: 2, xl: 4 }}>
          {PROOF.map((p) => (
            <Flex key={p.step} direction="column" gap="12px" p="32px 28px" bg={t.bg} minW={0}>
              <Flex justify="space-between" align="center" gap="8px" wrap="wrap">
                <Mono letterSpacing=".12em" color={alpha(t.text, 0.3)}>
                  {p.step}
                </Mono>
                <Badge
                  fontSize="2xs"
                  px={2}
                  borderRadius="full"
                  textTransform="uppercase"
                  bg={p.shipped ? t.greenTint : t.grayTint}
                  color={p.shipped ? t.green : t.textSoft}
                  borderWidth="1px"
                  borderColor={p.shipped ? t.greenBorder : t.grayBorder}
                  title={
                    p.shipped
                      ? "This step is in production today."
                      : "This step is a research direction. It is not shipped and is not promised on any plan."
                  }
                >
                  {p.shipped ? "In production" : "Research direction"}
                </Badge>
              </Flex>
              <H3 fontSize="1.15rem">{p.title}</H3>
              <Body fontSize=".92rem">{p.body}</Body>
            </Flex>
          ))}
        </CellGrid>

        <Body mt="26px" fontSize=".9rem" color={t.textFaint} maxW="760px">
          This is a research direction, not a shipped feature. Steps 01 and 02 are in production today; steps 03 and 04
          are what your engagement funds.
        </Body>
      </Inner>
    </Section>
  );
};
